import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Globe, ArrowRight } from "lucide-react";
import { COMPANY, PROJECT_TYPES } from "../data";
import { useReveal } from "../hooks/useReveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", project_type: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ref, inView] = useReveal();

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.message) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare l'informativa sulla privacy per inviare la richiesta.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm(EMPTY);
      setConsent(false);
    } catch (err) {
      toast.error("Invio non riuscito. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/30 py-3 text-base font-light text-white caret-white focus:border-white focus:outline-none transition-colors duration-300 placeholder:text-white/50";

  return (
    <section id="contatti" data-testid="contact-section" className="py-24 md:py-32 px-6 md:px-12 bg-black text-white">
      <div ref={ref} className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div className={`reveal ${inView ? "in-view" : ""}`}>
          <p className="text-xs uppercase tracking-[0.35em] text-champagne mb-5">Contatti</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-tight mb-8 text-center lg:text-left">
            Richiedi un preventivo gratuito.
          </h2>
          <p className="text-white/60 font-light leading-relaxed mb-12 max-w-md text-center lg:text-left mx-auto lg:mx-0">
            Descrivici il tuo progetto e le caratteristiche dell'intervento senza impegno e, se la proposta sarà di tuo interesse, organizzeremo un sopralluogo per definire tutti i dettagli.
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4" data-testid="contact-address">
              <MapPin strokeWidth={1} className="w-5 h-5 mt-1 text-champagne" />
              <div className="font-light text-white/80">
                <p>{COMPANY.legalName}</p>
                <p>{COMPANY.address}</p>
                <p className="text-sm text-white/50 mt-1">P. IVA: {COMPANY.piva} · C.F.: {COMPANY.cf}</p>
              </div>
            </div>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 link-underline w-fit" data-testid="contact-email">
              <Mail strokeWidth={1} className="w-5 h-5 text-champagne" />
              <span className="font-light text-white/80">{COMPANY.email}</span>
            </a>
            <a href={`mailto:${COMPANY.pec}`} className="flex items-center gap-4 link-underline w-fit" data-testid="contact-pec">
              <Mail strokeWidth={1} className="w-5 h-5 text-champagne" />
              <span className="font-light text-white/80">PEC · {COMPANY.pec}</span>
            </a>
            <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-4 link-underline w-fit" data-testid="contact-phone">
              <Phone strokeWidth={1} className="w-5 h-5 text-champagne" />
              <span className="font-light text-white/80">{COMPANY.phone}</span>
            </a>
            <a href={`https://${COMPANY.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 link-underline w-fit" data-testid="contact-website">
              <Globe strokeWidth={1} className="w-5 h-5 text-champagne" />
              <span className="font-light text-white/80">{COMPANY.website}</span>
            </a>
            <a href="https://wa.me/393791951206" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 link-underline w-fit" data-testid="contact-whatsapp">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#25D366]" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span className="font-light text-white/80">WhatsApp · +39 379 1951206</span>
            </a>

            <div className="pt-4" data-testid="contact-map">
              <iframe
                title="Mappa VETRO DESIGN ITALIA"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2894.183942497952!2d13.54177617652432!3d43.49849816242246!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1332798cc5f293df%3A0x2da40786adb53a20!2sVETRO%20DESIGN%20ITALIA%20SRL!5e0!3m2!1sit!2sit!4v1784306264213!5m2!1sit!2sit"
                className="w-full h-64 border border-white/15 grayscale-[0.3]"
                style={{ filter: "invert(0)" }}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className={`reveal ${inView ? "in-view" : ""}`} style={{ animationDelay: "0.15s" }}>
          <div className="space-y-8">
            <input
              data-testid="input-name"
              className={inputCls}
              placeholder="Nome e Cognome *"
              value={form.name}
              onChange={(e) => onChange("name", e.target.value)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <input
                data-testid="input-email"
                type="email"
                className={inputCls}
                placeholder="Email *"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
              />
              <input
                data-testid="input-phone"
                className={inputCls}
                placeholder="Telefono"
                value={form.phone}
                onChange={(e) => onChange("phone", e.target.value)}
              />
            </div>

            <Select value={form.project_type} onValueChange={(v) => onChange("project_type", v)}>
              <SelectTrigger
                data-testid="select-project-type"
                className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base font-light text-white focus:ring-0 focus:border-white data-[placeholder]:text-white/50"
              >
                <SelectValue placeholder="Tipo di Progetto *" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`option-${t}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <textarea
              data-testid="input-message"
              rows={4}
              className={`${inputCls} resize-none`}
              placeholder="Descrivi il tuo progetto *"
              value={form.message}
              onChange={(e) => onChange("message", e.target.value)}
            />

            <label className="flex items-start gap-3 cursor-pointer" data-testid="privacy-consent-label">
              <input
                type="checkbox"
                data-testid="privacy-consent-checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-champagne cursor-pointer"
              />
              <span className="text-xs text-white/60 font-light leading-relaxed text-left">
                Dichiaro di aver preso visione dell'
                <Link to="/privacy-policy" className="underline hover:text-white" data-testid="consent-privacy-link">informativa privacy</Link>
                {" "}e acconsento al trattamento dei dati per la gestione della mia richiesta.
              </span>
            </label>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-sm uppercase tracking-wide hover:gap-5 transition-all duration-300 disabled:opacity-50"
            >
              {loading ? "Invio in corso..." : "Invia Richiesta"}
              <ArrowRight strokeWidth={1.2} className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
